import { PaginatedResponse, PaginationSchema, Response } from '@features/state';
import request from '@utils/request';
import { PackageSchema } from '@features/DynamicPackagesConfigurationWizard/types';
import {
  AvailableRateSchema,
  RawPackageSchema,
  RoomRateSchema,
  SingleAvailableRateSchema,
  StaticRateSchema,
} from './schemas';
import {
  AvailabilityMode,
  getAvailableRoomById,
  removeRoomConfigDuplates,
  toAvailabilityModeParam,
} from '../rooms/requests';
import { MultiRoomSearch } from 'shared-types';
import { RatesFilterType } from './types';
import { getCommonItemsFromAvailability } from '../rooms/helpers/getCommonItemsFromAvailability.helper';
import { some } from '@utils/some';
import { toCamelCase } from 'case-converter';
import { getTotalPricesForRooms } from '../rooms/helpers/getTotalPricesForRooms.helper';
import { toSnakeCase } from 'case-converter';
import { getTotalFromPricesForRates } from './helpers/getTotalFromPricesForRates.helper';
import { RoomAndRatePageSettingsSchema } from '../rooms/schemas';
import { dropBrokenPackage, hasBrokenPackage } from './helpers/dropBrokenPackage.helper';

const ListedPackageSchema = RawPackageSchema.superRefine((rawPackage, context) => {
  if (!PackageSchema.safeParse(toCamelCase(rawPackage)).success) {
    context.addIssue({ code: 'custom', message: 'The package fails the booking schema' });
  }
});

const checkListedRatePackage = <T extends { package?: unknown }>(rate: T) =>
  dropBrokenPackage(rate, ListedPackageSchema, { from_price: null });

const checkRatePackage = <T extends { package?: unknown } | null>(rate: T) =>
  dropBrokenPackage(rate, PackageSchema, { rooms: [] });

export async function getStaticRates({
  siteSlug,
  type,
  language,
  ...params
}: Pick<RateParamsWithCodes, 'promoRateCode'> & {
  siteSlug: string;
  type?: RatesFilterType;
  language?: string;
}) {
  let query = '';

  const searchParams = new URLSearchParams();

  if (params && params.promoRateCode) {
    searchParams.set('promo-rate-code', params.promoRateCode);
  }

  if (type) {
    searchParams.set('type', type);
  }

  if (language) {
    searchParams.set('language', language);
  }

  const queryString = searchParams.toString();
  if (queryString) {
    query = `?${queryString}`;
  }

  const response = await request.v2.get(`/store/pages/site/${siteSlug}/rates${query}`);
  return PaginatedResponse(StaticRateSchema).parse(response.data);
}

export async function getStaticRate({
  rateCode,
  siteSlug,
  language,
}: {
  rateCode: string;
  siteSlug: string;
  language?: string;
}) {
  const query = language ? `?${new URLSearchParams({ language }).toString()}` : '';
  const response = await request.v2.get(
    `/store/pages/site/${siteSlug}/rate/rate-code/${rateCode}${query}`
  );
  return Response(StaticRateSchema).parse(response.data);
}

export async function getAvailableRates({
  siteId,
  rooms: searchRooms,
  isMultiRoomsEnabled = false,
  type,
  availabilityMode,
  ...params
}: RateParamsWithCodes & {
  siteId: number;
  isMultiRoomsEnabled?: boolean;
  type?: RatesFilterType;
  availabilityMode?: AvailabilityMode;
}) {
  const typeParam = type ? `&type=${type}` : '';
  const availabilityModeParam = toAvailabilityModeParam(availabilityMode);
  if (!isMultiRoomsEnabled) {
    const urlParams = buildQueryParams(params);
    const response = await request.base.get(
      `${siteId}/rates?${urlParams}&per-page=0${typeParam}${availabilityModeParam}`
    );
    return PaginationSchema(AvailableRateSchema).parse({
      ...response.data,
      data: Array.isArray(response.data?.data)
        ? response.data.data.map(checkListedRatePackage)
        : response.data?.data,
    });
  } else {
    const roomsWithoutDuplicateConfigs = removeRoomConfigDuplates(searchRooms);
    const meta = { page: 0, per_page: 0 };
    const brokenPackageRateIds = new Set<string>();
    const allAvailableRates = await Promise.all(
      roomsWithoutDuplicateConfigs.map(async ({ room, count }) => {
        const urlParams = buildQueryParams({
          ...params,
          adults: room.adults,
          children: room.children,
        });
        const response = await request.base.get(
          `${siteId}/rates?${urlParams}&per-page=0${typeParam}${availabilityModeParam}`
        );
        meta.page = Math.max(meta.page, response.data.page);
        meta.per_page = Math.max(meta.per_page, response.data.per_page);
        return response.data.data.map((rate) => {
          if (hasBrokenPackage(rate, ListedPackageSchema)) {
            brokenPackageRateIds.add(rate.id);
          }
          return { ...checkListedRatePackage(rate), count };
        });
      })
    );

    const commonRates = getCommonItemsFromAvailability(allAvailableRates, searchRooms, false);
    const fromPrices = getTotalFromPricesForRates(allAvailableRates);

    return PaginationSchema(AvailableRateSchema).parse({
      total: commonRates.length,
      data: commonRates.map((rate) => {
        const combinedRate = { ...rate, ...fromPrices.get(rate.id) };
        return brokenPackageRateIds.has(rate.id)
          ? { ...combinedRate, package: null, from_price: null }
          : combinedRate;
      }),
      ...meta,
    });
  }
}

export async function getAvailableRate(
  rateCode: string,
  { siteId, rooms: searchRooms, ...params }: RateParamsWithCodes & { siteId: number },
  isMultiRoomsEnabled = false
) {
  if (!isMultiRoomsEnabled) {
    const urlParams = buildQueryParams(params);
    const response = await request.base.get(`/${siteId}/rates/${rateCode}?${urlParams}`);
    return SingleAvailableRateSchema.parse(checkRatePackage(toCamelCase(response.data)));
  } else {
    const roomsWithoutDuplicateConfigs = removeRoomConfigDuplates(searchRooms);
    const allResponses = await Promise.all(
      roomsWithoutDuplicateConfigs.map(async ({ room, count }) => {
        const urlParams = buildQueryParams({
          ...params,
          adults: room.adults,
          children: room.children,
        });
        //for each room configuration make the request
        const response = await request.base.get(`/${siteId}/rates/${rateCode}?${urlParams}`);

        const rate = SingleAvailableRateSchema.parse(checkRatePackage(toCamelCase(response.data)));

        return rate
          ? {
              ...rate,
              rooms: rate.rooms.map((r) => ({ ...r, count })),
            }
          : null;
      })
    );
    const definedResponses = allResponses.filter((r) => !!r);
    if (!some(definedResponses) || allResponses.length !== definedResponses.length) {
      return null;
    }
    const rate = definedResponses[0];
    if (rate) {
      const definedRateRooms = definedResponses.map((r) => r?.rooms || []);
      //map rooms pricing to be the total for all search rooms
      const totalPricesMap = getTotalPricesForRooms(toSnakeCase(definedRateRooms));
      rate.rooms = getCommonItemsFromAvailability(definedRateRooms, searchRooms).map((room) => {
        return {
          ...room,
          ...toCamelCase(totalPricesMap.get(room.id)),
        };
      });
    }
    return rate;
  }
}

export async function getRoomRates(roomId: string, params: RateParams & { siteId: number }) {
  const room = await getAvailableRoomById({ ...params, id: roomId });
  return RoomRateSchema.parse(room);
}

export type RateParams = {
  fromDate: string;
  toDate: string;
  adults: number;
  children: number;
  childAges: { [child: string]: number };
  rooms: MultiRoomSearch;
  promoRateCode?: string;
};

export type RateParamsWithCodes = RateParams & {
  promoRateCode?: string;
};

export function buildQueryParams(params: Omit<RateParams, 'rooms'> | RateParamsWithCodes) {
  const childAgesValues = Object.values(params.childAges);
  const childAgesParam = childAgesValues.length
    ? { 'child-ages': childAgesValues.join(',') }
    : undefined;

  const searchParams = new URLSearchParams({
    'from-date': params.fromDate,
    'to-date': params.toDate,
    adults: params.adults.toString(),
    children: params.children.toString(),
    ...childAgesParam,
  });

  if ('promoRateCode' in params && params.promoRateCode) {
    searchParams.append('promo-rate-code', params.promoRateCode);
  }

  const urlParams = searchParams.toString();

  return urlParams;
}

export async function getRatePageSettings({
  siteId,
  language,
}: {
  siteId: number;
  language: string;
}) {
  const response = await request.base.get(`${siteId}/pages/rates/${language}`);
  if (response.status === 204) {
    return null;
  }
  return RoomAndRatePageSettingsSchema.parse(response.data);
}
